import { useMemo } from 'react';
import { useGLTF } from '@react-three/drei';
import { RigidBody, CapsuleCollider } from '@react-three/rapier';
import * as THREE from 'three';

interface TreeProps {
    position: [number, number, number];
    seed?: number;
}

export const Tree = ({ position, seed = 0 }: TreeProps) => {
    const pine = useGLTF('/assets/Blender/GLB/PineTree01.glb');
    const birch = useGLTF('/assets/Blender/GLB/BirchTree.glb');
    const gen = useGLTF('/assets/Blender/GLB/GenTree01.glb');

    const { scene, scale, yOffset, collider } = useMemo(() => {
        const scenes = [pine.scene, birch.scene, gen.scene];
        const index = Math.abs(Math.floor(seed)) % scenes.length;
        const scaleVariants = [0.9, 1.05, 1.2];
        const scaleIndex = Math.abs(Math.floor(seed * 7)) % scaleVariants.length;
        const scaleValue = scaleVariants[scaleIndex];

        const base = scenes[index];
        const box = new THREE.Box3().setFromObject(base);
        const size = new THREE.Vector3();
        box.getSize(size);
        const yOffsetValue = -box.min.y * scaleValue;

        const trunkRadius = Math.max(0.2, Math.min(size.x, size.z) * 0.12 * scaleValue);
        const trunkHeight = Math.max(1, size.y * 0.45 * scaleValue);

        return {
            scene: base,
            scale: scaleValue,
            yOffset: yOffsetValue, // This usually corrects base to 0
            collider: { halfHeight: trunkHeight * 0.5, radius: trunkRadius }
        };
    }, [pine.scene, birch.scene, gen.scene, seed]);

    // Apply manual downward shift to fix floating issue once and for all
    // If yOffset centers the base to 0, then position handles placement.
    // If trees float, it means the perceived base is higher than 0.
    // Let's brute force it down by another unit or two. Trees can have roots underground.
    const FIX_FLOAT = -2.5; 

    return (
        <RigidBody type="fixed" colliders={false} position={position}>
            <group position={[0, yOffset + FIX_FLOAT, 0]}>
                <primitive object={scene.clone(true)} scale={scale} />
            </group>
            <CapsuleCollider args={[collider.halfHeight, collider.radius]} position={[0, collider.halfHeight + FIX_FLOAT, 0]} />
        </RigidBody>
    );
};

useGLTF.preload('/assets/Blender/GLB/PineTree01.glb');
useGLTF.preload('/assets/Blender/GLB/BirchTree.glb');
useGLTF.preload('/assets/Blender/GLB/GenTree01.glb');
